import { Injectable, NgZone, inject } from '@angular/core';
import { trace } from '@opentelemetry/api';
import { contexteUtilisateur } from '@services/core/telemetrie';

/** Le décalage n'apparaît qu'une fois le clavier fermé ou l'appareil photo refermé. */
const VERIFICATIONS_MS = [150, 500, 1200];
/** Temps laissé à l'écran pour se remettre en place avant de mesurer le résultat. */
const CONTROLE_MS = 300;
/** La relance de la zone d'affichage est plus visible qu'un défilement : jamais en rafale. */
const REPOS_RELANCE_MS = 5000;
const SEUIL_PX = 1;

const CHAMP_SAISIE = 'input:not([type=file]):not([type=checkbox]):not([type=radio]),textarea,select,[contenteditable=""],[contenteditable=true]';

interface Mesure {
	scrollY: number;
	offsetTop: number;
	echelle: number;
	hauteurFenetre: number;
	hauteurVue: number;
}

function estIos(): boolean {
	const ua = navigator.userAgent;
	return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
}

/**
 * Touchers décalés vers le bas sur iOS (ATLAS-252) : on touche un bouton, c'est celui du
 * dessous qui réagit, partout dans l'appli, jusqu'à tourner l'appareil ou changer d'onglet.
 *
 * Bug WebKit (297779, 191204), iOS 26 et 27 : après le clavier ou l'appareil photo, iOS
 * laisse la page décalée alors que l'affichage paraît correct. Or la page d'Atlas ne
 * défile jamais en entier (ce sont les zones internes qui défilent, mesuré écran par
 * écran) : tout décalage de la page est donc anormal et on la remet à zéro. Si le
 * décalage résiste, on relance la zone d'affichage, ce que fait la rotation.
 *
 * Chaque recalage part en télémétrie (`ecran.recalage`) : le bug n'existe que sur un vrai
 * appareil, c'est le seul moyen de vérifier que le correctif agit. Un contrôle part aussi
 * après chaque photo, décalage ou non : s'il reste à zéro alors que le problème est encore
 * signalé, c'est que la cause est ailleurs.
 */
@Injectable({ providedIn: 'root' })
export class RecalageEcranService {
	private zone = inject(NgZone);
	private demarre = false;
	private attentes: number[] = [];
	private derniereRelance = 0;

	demarrer(): void {
		if (this.demarre || !estIos()) return;
		this.demarre = true;
		// Hors Angular : ces événements ne doivent pas relancer la détection de changements.
		this.zone.runOutsideAngular(() => {
			const vue = window.visualViewport;
			document.addEventListener('focusout', () => this.planifier('clavier'), { passive: true });
			vue?.addEventListener('resize', () => this.planifier('ecran'), { passive: true });
			vue?.addEventListener('scroll', () => this.planifier('ecran'), { passive: true });
			// Sans capture : seul le défilement de la page arrive ici, pas celui des zones internes.
			window.addEventListener('scroll', () => this.planifier('defilement'), { passive: true });
			window.addEventListener('focus', () => this.planifier('retour'), { passive: true });
			window.addEventListener('pageshow', () => this.planifier('retour'), { passive: true });
			document.addEventListener('visibilitychange', () => this.planifier('retour'), { passive: true });
			// Photo prise, choisie ou annulée. En capture : le champ se retire de la page aussitôt.
			const retourPhoto = (e: Event) => {
				if ((e.target as HTMLElement | null)?.matches?.('input[type=file]')) this.planifier('photo');
			};
			document.addEventListener('change', retourPhoto, { capture: true, passive: true });
			document.addEventListener('cancel', retourPhoto, { capture: true, passive: true });
		});
	}

	private planifier(declencheur: string): void {
		this.attentes.forEach(a => clearTimeout(a));
		this.attentes = VERIFICATIONS_MS.map((delai, i) => window.setTimeout(
			() => this.verifier(declencheur, i === VERIFICATIONS_MS.length - 1), delai,
		));
	}

	private verifier(declencheur: string, derniere: boolean): void {
		// Clavier ouvert : iOS décale la page exprès, pour montrer le champ.
		if (document.hidden || document.activeElement?.matches(CHAMP_SAISIE)) return;
		const avant = this.mesurer();
		// Pincement : le décalage est celui du zoom voulu par l'utilisateur.
		if (avant.echelle > 1.01) return;
		if (!this.estDecale(avant)) {
			if (declencheur === 'photo' && derniere) this.signaler(declencheur, 'aucune', avant, avant);
			return;
		}

		window.scrollTo(0, 0);
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;
		window.setTimeout(() => {
			if (!this.estDecale(this.mesurer())) {
				this.signaler(declencheur, 'defilement', avant, this.mesurer());
				return;
			}
			if (Date.now() - this.derniereRelance < REPOS_RELANCE_MS) {
				this.signaler(declencheur, 'en_attente', avant, this.mesurer());
				return;
			}
			this.derniereRelance = Date.now();
			this.relancerAffichage();
			window.setTimeout(() => this.signaler(declencheur, 'relance', avant, this.mesurer()), CONTROLE_MS);
		}, CONTROLE_MS);
	}

	private mesurer(): Mesure {
		const vue = window.visualViewport;
		return {
			scrollY: window.scrollY,
			offsetTop: vue?.offsetTop ?? 0,
			echelle: vue?.scale ?? 1,
			hauteurFenetre: window.innerHeight,
			hauteurVue: vue?.height ?? window.innerHeight,
		};
	}

	private estDecale(m: Mesure): boolean {
		return Math.abs(m.scrollY) >= SEUIL_PX || Math.abs(m.offsetTop) >= SEUIL_PX;
	}

	/** Réécrit la zone d'affichage le temps de deux images : iOS la recalcule, comme à la rotation. */
	private relancerAffichage(): void {
		const balise = document.querySelector<HTMLMetaElement>('meta[name=viewport]');
		if (!balise) return;
		const contenu = balise.content;
		balise.content = contenu.replace('initial-scale=1', 'initial-scale=0.999');
		requestAnimationFrame(() => requestAnimationFrame(() => { balise.content = contenu; }));
	}

	private signaler(declencheur: string, methode: string, avant: Mesure, apres: Mesure): void {
		try {
			const span = trace.getTracer('atlas-v2-web').startSpan('ecran.recalage');
			const utilisateur = contexteUtilisateur();
			if (utilisateur.username) span.setAttribute('user.name', utilisateur.username);
			span.setAttribute('recalage.declencheur', declencheur);
			span.setAttribute('recalage.methode', methode);
			span.setAttribute('recalage.scroll_y', Math.round(avant.scrollY));
			span.setAttribute('recalage.offset_top', Math.round(avant.offsetTop));
			span.setAttribute('recalage.apres_scroll_y', Math.round(apres.scrollY));
			span.setAttribute('recalage.apres_offset_top', Math.round(apres.offsetTop));
			span.setAttribute('recalage.hauteur_fenetre', Math.round(avant.hauteurFenetre));
			span.setAttribute('recalage.hauteur_vue', Math.round(avant.hauteurVue));
			span.setAttribute('recalage.installee', window.matchMedia?.('(display-mode: standalone)').matches ?? false);
			span.setAttribute('recalage.ios', /OS (\d+[_\d]*)/.exec(navigator.userAgent)?.[1]?.replace(/_/g, '.') ?? '');
			span.setAttribute('recalage.appareil', /iPad|iPhone|iPod/.exec(navigator.userAgent)?.[0] ?? 'iPad');
			span.end();
		} catch {
			// Mesure seulement : ne jamais gêner l'appli.
		}
	}
}
